<script setup lang="ts">
import type { UserDocument } from '@/types/UserData';
import type AntiRobot from '@/components/base/form/AntiRobot.vue';

defineProps({
  userHasDocument: {
    type: Boolean,
    required: true,
  },
});

type Emits = {
  updateUserDocumentCpf: [UserDocument];
};

const emit = defineEmits<Emits>();

const userDocument = ref({
  docnumber: '',
  docnumber_valid: false,
  full_name: '',
  birth_date: '',
  gender: '',
  mother_name: '',
});

function onValidateDocument(data: UserDocument) {
  userDocument.value.docnumber_valid = true;

  if (data.name) {
    userDocument.value.full_name = data.name;
  }
  if (data.birth_date) {
    userDocument.value.birth_date = data.birth_date;
  }
  if (data.gender) {
    userDocument.value.gender = data.gender;
  }
  if (data.mother_name) {
    userDocument.value.mother_name = data.mother_name;
  }

  emit('updateUserDocumentCpf', userDocument.value);
}

function resetAntiRobotToken() {
  antiRobotRef.value?.$resetAllCaptcha();
}

const antiRobotRef = ref<InstanceType<typeof AntiRobot> | null>(null);
const antiRobotToken = computed<string>(
  () => antiRobotRef.value?.finalCaptchaTokenValue ?? '',
);

function onRemoveDocument() {
  setTimeout(() => {
    userDocument.value.docnumber = '';
    userDocument.value.docnumber_valid = false;
    userDocument.value.full_name = '';
    userDocument.value.birth_date = '';
    userDocument.value.gender = '';
    userDocument.value.mother_name = '';
    emit('updateUserDocumentCpf', userDocument.value);
  }, 100);
}

defineExpose({
  antiRobotToken,
});
</script>

<template>
  <template v-if="!userHasDocument">
    <div :class="$style.inputDocument">
      <BaseFormInputCpf
        v-model:masked="userDocument.docnumber"
        :anti-robot-token="antiRobotToken"
        compact
        :placeholder="$t('deposit.errors.insert_cpf')"
        @remove-document="onRemoveDocument"
        @reset-anti-robot-token="resetAntiRobotToken"
        @validate-document="onValidateDocument"
      />
      <div
        :class="[
          $style.antirobot,
          { [$style.hideDiv]: userDocument.docnumber_valid },
        ]"
      >
        <BaseFormAntiRobot ref="antiRobotRef" />
      </div>
    </div>
  </template>
</template>

<style module lang="postcss">
.inputDocument {
  @apply relative bg-auth-bg-inputs py-1 rounded-md;
  .antirobot {
    @apply absolute top-0 right-0 z-0 opacity-30 whitespace-nowrap p-[3px] w-[170px];

    &.hideDiv {
      @apply hidden;
    }
  }
}
</style>
